import { useMemo } from 'react';
import {
  Box,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CardActionButton from 'src/components/CardActionButton';
import Iconify from 'src/components/Iconify';
import StatusChip from 'src/components/StatusChip';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { formatDate } from 'src/utils/formatTime';
import { useCanViewAutomatedDeploymentSettings } from 'src/utils/featureFlags';
import { getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';
import {
  getWdacPolicyPurposeLabel,
  getWdacPolicyPurposeOptions,
} from 'src/utils/enums/wdacPolicyPurpose';
import {
  getDeployableArtifactTypeColor,
  getDeployableArtifactTypeLabel,
  isDeployableArtifactCatalog,
} from 'src/utils/enums/deployableArtifactType';

const SMALL_CHIP_SX = { height: 20, fontSize: '0.65rem' };

const DEFAULT_CHIP_COLOR = '#7DFF92';

const POLICY_COLUMNS = [
  { key: 'policy', label: 'Policy', width: '32%' },
  { key: 'artifact', label: 'Artifact', width: '32%' },
  { key: 'version', label: 'Version', width: '12%' },
  { key: 'purpose', label: 'Purpose', width: '14%' },
  { key: 'created', label: 'Created', width: '10%' },
];

// Every purpose a base can be deployed for is shown whether or not it has been generated yet, so
// the card reads the same for a group that only covers part of the fleet's needs.
const PURPOSE_OPTIONS = getWdacPolicyPurposeOptions();

const TwoLineText = ({ primary, secondary }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
    <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: '#F3F4F6' }}>
      {primary}
    </Typography>
    <Typography variant="caption" noWrap sx={{ color: '#9CA3AF', fontSize: '0.65rem' }}>
      {secondary}
    </Typography>
  </Stack>
);

// The three sections all describe a policy reaching devices in some form, so they share one row
// shape: the policy it belongs to, the artifact carrying it, and what it is deployed for.
const PolicySection = ({ title, rows, emptyMessage, caption, onOpenPolicy }) => (
  <Stack
    gap={0.75}
    sx={{
      backgroundColor: 'rgba(0, 0, 0, 0.14)',
      border: '1px solid #5B5B5B',
      borderRadius: '8px',
      px: 1.25,
      py: 1,
    }}>
    <Typography
      sx={{
        color: '#9CA3AF',
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}>
      {title}
      {rows.length ? ` (${rows.length})` : ''}
    </Typography>
    {rows.length ? (
      <Table
        size="small"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { fontSize: '0.75rem', py: 0.5, borderBottom: 0 },
          '& .MuiTableHead-root .MuiTableCell-root': { borderBottom: '1px solid #565656' },
        }}>
        <colgroup>
          {POLICY_COLUMNS.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {POLICY_COLUMNS.map(column => (
              <TableCell key={column.key}>
                <Typography sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.68rem' }}>
                  {column.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ id, policy, artifact, purposeLabel }) => {
            const artifactColor = artifact
              ? getDeployableArtifactTypeColor(artifact.artifactType)
              : null;

            return (
              <TableRow
                key={id}
                hover
                onClick={() => onOpenPolicy(policy.id)}
                sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ minWidth: 0 }}>
                  <TwoLineText primary={policy.policyName} secondary={policy.policyId} />
                </TableCell>
                <TableCell sx={{ minWidth: 0 }}>
                  {artifact ? (
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                      <StatusChip
                        label={getDeployableArtifactTypeLabel(artifact.artifactType)}
                        color={artifactColor}
                        backgroundColor={`${artifactColor}1F`}
                        sx={SMALL_CHIP_SX}
                      />
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: '#9CA3AF', fontSize: '0.65rem' }}>
                        {/* A Win32 artifact is attached before the job that builds its file runs. */}
                        {artifact.fileName || artifact.artifactHash || 'Pending generation'}
                      </Typography>
                    </Stack>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{policy.policyVersion || '—'}</TableCell>
                <TableCell>{purposeLabel || '—'}</TableCell>
                <TableCell>
                  {formatDate(artifact ? artifact.createdAt : policy.createdAt, 'yyyy-MM-dd')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    ) : (
      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
        {emptyMessage}
      </Typography>
    )}
    {caption ? (
      <Typography variant="caption" sx={{ color: '#D1D5DB' }}>
        {caption}
      </Typography>
    ) : null}
  </Stack>
);

// One row per deployable artifact rather than per supplemental policy: a supplemental that has
// been compiled and signed shows each form it can be deployed in.
const splitArtifactRows = supplementalPolicies => {
  const policies = [];
  const catalogs = [];

  supplementalPolicies.forEach(policy => {
    (policy.artifacts || []).forEach(artifact => {
      (isDeployableArtifactCatalog(artifact.artifactType) ? catalogs : policies).push({
        id: artifact.id,
        policy,
        artifact,
        purposeLabel: getWdacPolicyPurposeLabel(policy.policyPurpose),
      });
    });
  });

  return { policies, catalogs };
};

const purposeAssignmentCount = (purposeCounts, purpose) =>
  purposeCounts.find(entry => entry.purpose === purpose)?.assignmentCount ?? 0;

const ManagedBasePolicyCard = ({ item, isExpanded, onToggleExpand, onEdit, onOpenPolicy }) => {
  const { basePolicy, purposeCounts = [], supplementalPolicies = [] } = item;
  const canViewDeploymentSettings = useCanViewAutomatedDeploymentSettings();

  const { policies, catalogs } = useMemo(
    () => splitArtifactRows(supplementalPolicies),
    [supplementalPolicies],
  );

  const baseRows = useMemo(
    () => [
      {
        id: `base-${basePolicy.id}`,
        policy: basePolicy,
        artifact: null,
        purposeLabel: 'Base Policy',
      },
    ],
    [basePolicy],
  );

  const mode = getBasePolicyModeLabel(basePolicy.basePolicyMode);

  return (
    <Stack sx={{ backgroundColor: '#454545', border: '1px solid #5B5B5B', borderRadius: '8px' }}>
      <Stack
        gap={0.75}
        onClick={() => onToggleExpand(basePolicy.id)}
        sx={{ p: 1.5, cursor: 'pointer' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <Stack gap={0.75} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Iconify icon="mdi:shield-check-outline" width={18} height={18} color="#87C8E0" />
              <Typography
                sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
                {basePolicy.policyName}
              </Typography>
              {basePolicy.environmentDefault ? (
                <Tooltip title="Pre-selected when generating supplemental components.">
                  <Box component="span" display="inline-flex">
                    <StatusChip
                      label="Default"
                      color={DEFAULT_CHIP_COLOR}
                      backgroundColor={`${DEFAULT_CHIP_COLOR}1F`}
                      sx={SMALL_CHIP_SX}
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>

            <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
              {mode ? `${mode} mode` : 'Mode unknown'}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
            <CardActionButton
              onClick={event => {
                event.stopPropagation();
                onEdit(basePolicy);
              }}
              startIcon={<Iconify icon="mdi:pencil-outline" width={15} height={15} />}>
              Edit
            </CardActionButton>
            <Iconify
              icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              width={20}
              height={20}
              color="#9CA3AF"
            />
          </Stack>
        </Stack>

        {/* The fields describe the whole base policy, so they run the card's width rather than
            sharing the header row with the actions and stopping short of them. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: `repeat(${canViewDeploymentSettings ? 5 : 3}, minmax(0, 1fr))`,
            },
            gap: 1.25,
            mt: 0.25,
          }}>
          {canViewDeploymentSettings ? (
            <>
              <SummaryValue compact label="Base policy deployment">
                {basePolicy.enableBasePolicyDeployment ? 'On' : 'Off'}
              </SummaryValue>
              <SummaryValue compact label="Supplemental policy deployment">
                {basePolicy.enableSupplementalPolicyDeployment ? 'On' : 'Off'}
              </SummaryValue>
            </>
          ) : null}
          {PURPOSE_OPTIONS.map(purpose => (
            <SummaryValue key={purpose.value} compact label={purpose.label}>
              {purposeAssignmentCount(purposeCounts, purpose.value)}
            </SummaryValue>
          ))}
        </Box>
      </Stack>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Stack gap={1} sx={{ px: 1.5, pb: 1.5 }}>
          <PolicySection
            title="Base policy"
            rows={baseRows}
            caption={basePolicy.policyDescription}
            onOpenPolicy={onOpenPolicy}
          />
          <PolicySection
            title="Supplemental policies"
            rows={policies}
            emptyMessage="No supplemental policies have been generated for this base policy yet."
            onOpenPolicy={onOpenPolicy}
          />
          <PolicySection
            title="Security catalogs"
            rows={catalogs}
            emptyMessage="No security catalogs have been generated for this base policy yet."
            onOpenPolicy={onOpenPolicy}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default ManagedBasePolicyCard;
